'use client';

import React from 'react';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Box, Typography } from '@mui/material';
import { AlertTriangle } from 'lucide-react';

import { useT } from '@/hooks';
import type { StreakStatus } from '@/types/streak.types';

interface StreakHeroProps {
  data: StreakStatus;
}

/**
 * StreakHero
 * Page hero — leads with the number that matters most (current streak) in
 * the same gradient-text treatment ReferralHeader uses, so the two dedicated
 * program pages read as one family. Surfaces the at-risk state right up top
 * since that's the single most time-sensitive thing on this page.
 */
export const StreakHero: React.FC<StreakHeroProps> = ({ data }) => {
  const { t } = useT();
  const hasStreak = data.consecutiveDays > 0;
  const amountRemaining = Math.max(0, data.todaysTarget - data.todaysVolume);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '0.7rem',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#e8b046',
        }}
      >
        <LocalFireDepartmentIcon sx={{ fontSize: 16 }} />
        <span>{t('streaks.eyebrow', { defaultValue: 'Trading Streak' })}</span>
      </Box>

      {hasStreak ? (
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            letterSpacing: '-0.02em',
            fontSize: { xs: '2.25rem', md: '3rem' },
            background: 'linear-gradient(90deg, #e8b046, #e0664f)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('streaks.heroActive', { defaultValue: `${data.consecutiveDays}-day streak` })}
        </Typography>
      ) : (
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            letterSpacing: '-0.02em',
            color: 'text.primary',
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {t('streaks.heroEmpty', { defaultValue: 'Start your streak today' })}
        </Typography>
      )}

      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', maxWidth: 640, fontSize: '1.0625rem', lineHeight: 1.55 }}
      >
        {hasStreak
          ? t('streaks.heroSubActive', {
              defaultValue:
                'Trade a little every day to keep it alive — the reward grows the longer you run it.',
            })
          : t('streaks.heroSubEmpty', {
              defaultValue:
                "Hit today's trading target and come back tomorrow to start a streak — points, badges, and a leaderboard spot come with it.",
            })}
      </Typography>

      {data.isAtRisk && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mt: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'error.main',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(224, 102, 79, 0.10)' : 'rgba(224, 102, 79, 0.06)',
            maxWidth: 560,
          }}
        >
          <AlertTriangle size={18} color="#e0664f" style={{ flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
            {t('streaks.atRiskBanner', {
              defaultValue: `Your streak expires tonight — trade $${amountRemaining.toFixed(2)} more to keep it alive.`,
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
